import { useEffect, useRef, useState } from 'react';
import { LANGUAGES, languageByCode, DEFAULT_LANGUAGE } from '../util/languages.js';

// T140 — Compact, accessible language picker: a button showing the current flag +
// native name, and a popover listbox of all supported languages (flag +
// native name + English name). Chooses the OUTPUT language of generated
// documents in the cover-letter and tailored-CV modals.
//
// Built bespoke (the project has no headless-ui). Closes on click-outside
// and Escape — Escape is swallowed while open so it doesn't also close the
// parent Modal. Keyboard: ↑/↓/Home/End move the active option, Enter/Space
// selects; aria-activedescendant tracks it for screen readers.

export default function LanguageSelect({ value, onChange, id = 'language-select', disabled = false }) {
  const current = languageByCode(value) ?? languageByCode(DEFAULT_LANGUAGE);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(0, LANGUAGES.findIndex((l) => l.code === current.code)),
  );
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  // Click outside closes the menu.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the active option scrolled into view while arrowing.
  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.querySelector(`[data-idx="${activeIndex}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const openMenu = () => {
    setActiveIndex(Math.max(0, LANGUAGES.findIndex((l) => l.code === current.code)));
    setOpen(true);
  };

  const select = (code) => {
    onChange?.(code);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        e.stopPropagation(); // don't let the parent Modal close as well
        setOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % LANGUAGES.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + LANGUAGES.length) % LANGUAGES.length);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(LANGUAGES.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        select(LANGUAGES[activeIndex].code);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const listId = `${id}-listbox`;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${id}-opt-${activeIndex}` : undefined}
        className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100 transition hover:border-brand-600 focus:border-brand-500 focus:outline-none disabled:opacity-50"
        title="Language of the generated document"
      >
        <span aria-hidden="true" className="text-base leading-none">{current.flag}</span>
        <span className="font-medium">{current.endonym}</span>
        <span aria-hidden="true" className={'ml-0.5 text-slate-500 transition-transform ' + (open ? 'rotate-180' : '')}>▾</span>
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-label="Document language"
          className="absolute left-0 top-full z-50 mt-1 max-h-64 w-56 overflow-y-auto rounded-md border border-slate-700 bg-slate-900 py-1 shadow-2xl ring-1 ring-black/20"
        >
          {LANGUAGES.map((l, idx) => {
            const isSelected = l.code === current.code;
            const isActive = idx === activeIndex;
            return (
              <li
                key={l.code}
                id={`${id}-opt-${idx}`}
                data-idx={idx}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => select(l.code)}
                className={
                  'flex cursor-pointer items-center gap-2.5 px-3 py-1.5 text-sm ' +
                  (isActive ? 'bg-brand-600/20 text-slate-100' : 'text-slate-300')
                }
              >
                <span aria-hidden="true" className="text-base leading-none">{l.flag}</span>
                <span className="font-medium">{l.endonym}</span>
                <span className="text-xs text-slate-500">{l.name}</span>
                {isSelected && <span aria-hidden="true" className="ml-auto text-brand-400">✓</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
